// The Stats tab's range presets and the date maths behind them.
//
// ⚠️ WHY THIS IS ITS OWN MODULE. It used to live inside StatsTab.jsx, where it could not be
// tested without bundling the whole component — which drags in the Supabase client. On
// 2026-08-30 the calendar button crashed the entire tab because this function could return
// `from: ''`, and StatsTab does `new Date(from + 'T00:00:00').toISOString()` DURING RENDER:
// 'T00:00:00' is an Invalid Date and toISOString() throws RangeError. A pure function whose
// only job is to guarantee two usable dates belongs somewhere a plain node test can reach it.
// See statsRange.test.mjs — every case there has a control that fails on the old behaviour.
//
// THE ONE RULE: getRangeDates must ALWAYS return two strings that `new Date(s + 'T00:00:00')`
// can parse, for every input including empty strings and ids that no longer exist. There is no
// input for which throwing is better than a sane default, because the caller renders with it.

// ─── Date helpers ─────────────────────────────────────────────────────────────
// Local components on purpose. toISOString() is UTC and Georgia is UTC+4, so between 00:00
// and 04:00 it reports yesterday — which silently shifts every window by a day.
export function toStr(d) {
  const y = d.getFullYear(), m = String(d.getMonth()+1).padStart(2,'0'), dd = String(d.getDate()).padStart(2,'0')
  return `${y}-${m}-${dd}`
}
export function today() { return toStr(new Date()) }

function daysAgo(n) { const d = new Date(); d.setDate(d.getDate() - n); return toStr(d) }

// n days before an ISO yyyy-mm-dd. Falls back to counting from today if the string cannot be
// parsed, so this can never be the thing that returns an unusable date.
function minusDays(dateStr, n) {
  const t = new Date(dateStr + 'T00:00:00')
  if (Number.isNaN(t.getTime())) return daysAgo(n)
  t.setDate(t.getDate() - n)
  return toStr(t)
}

// `agentOk` marks what a capped role may choose, INSTEAD of deriving it from `days <= 30`.
// "This month" has no fixed length — it is 1 day on the 1st and 31 on the 31st — so a
// day-count rule would have silently excluded it from the agent list on most days of the
// month and included it on none. Say which are allowed; do not infer it.
export const QUICK_RANGES = [
  // Labels are i18n KEYS (2026-09-06): the buttons used to be Georgian literals, so an
  // English-language viewer got a Georgian range bar on an otherwise English page.
  { id: '7d',  labelKey: 'stats_range_7d',  days: 7,    agentOk: true },
  { id: '30d', labelKey: 'stats_range_30d', days: 30,   agentOk: true },
  // Calendar month to date — the payroll question, and not the same as the rolling 30 days
  // beside it. Both are kept because they answer different things: "the last month of work"
  // versus "what this month has paid so far".
  { id: 'mtd', labelKey: 'stats_range_mtd', days: null, agentOk: true, monthToDate: true },
  { id: '90d', labelKey: 'stats_range_90d', days: 90   },
  { id: '6m',  labelKey: 'stats_range_6m',  days: 180  },
  { id: '1y',  labelKey: 'stats_range_1y',  days: 365  },
  // ⚠️ NO 'ყველა' PRESET (owner, 2026-08-29). Seven buttons plus the calendar overflowed the
  // range bar on a phone — the last preset was clipped at the right edge. The calendar picker
  // already covers "everything": it opens on the same row and can be set to any start date,
  // so the button was width nobody could afford for a window nobody could otherwise reach.
  // If you add a preset here, check the bar on a ~390px viewport first.
]

const DEFAULT_DAYS = 90

export function getRangeDates(rangeId, customFrom, customTo) {
  if (rangeId === 'custom') {
    // ⚠️ BOTH SIDES MUST FALL BACK, and `from` is the one that bit us (owner, 2026-08-30:
    // "pressing on calendar icon gives error"). The pickers start EMPTY, so the instant the
    // calendar was pressed `from` was '' and the tab went blank. A default window keeps every
    // downstream date valid no matter what the inputs hold; whichever side the user HAS
    // filled in is still respected.
    const to = customTo || today()
    // ⚠️ ANCHOR THE FALLBACK START TO `to`, NOT TO TODAY. If the user has typed only an end
    // date of, say, 20 Feb, "90 days before today" lands in June — a backwards window whose
    // only repair is to swap the ends, which silently moves the date they typed from the end
    // of the window to the start. Ninety days ending on their date is what they meant, and it
    // keeps their input where they put it.
    const from = customFrom || minusDays(to, DEFAULT_DAYS)
    // An INVERTED window is worse than a wrong one: every query returns nothing, so the tab
    // renders a full, confident "no results" and gives the user no clue that the dates are
    // backwards. It is reachable by filling only the end date with a past day (the fallback
    // start is 90 days ago), and by simply typing the two dates in the other order. Both
    // strings are ISO yyyy-mm-dd, so a lexical compare is a date compare.
    return from > to ? { from: to, to: from } : { from, to }
  }
  // 'all' is no longer offered as a preset but the branch stays: it is exactly the shape of id
  // that outlives its entry, and without it the lookup below returns undefined.
  if (rangeId === 'all') return { from: '2020-01-01', to: today() }
  if (rangeId === 'mtd') {
    const d = new Date(); d.setDate(1)
    return { from: toStr(d), to: today() }
  }
  const r = QUICK_RANGES.find(x => x.id === rangeId)
  // ⚠️ An id with no entry must DEGRADE, not throw. `r.days` on undefined is a TypeError, and
  // this runs during render — so a removed preset (see 'ყველა' above) would take the tab down
  // for anyone whose state still held it. Falling back to the default window is always better
  // than a blank screen, and the range bar will show nothing selected, which is honest.
  return { from: daysAgo(r?.days ?? DEFAULT_DAYS), to: today() }
}

// An AGENT sees nothing from before their own hire (owner, 2026-09-06: "newly hired agent must
// only see what happened after their hiring, not past"). The window's start is lifted to the
// hire date; the end is untouched. Returns whether the clamp bit, so the caller can drop the
// previous-period arrows — a "previous period" for someone hired three weeks ago is a window in
// which they did not exist, and an arrow against it would be a comparison with nothing.
// A hire date after `to` collapses the window to the single day `to` rather than inverting it.
export function clampToHire({ from, to }, hireDate) {
  if (!hireDate || hireDate <= from) return { from, to, clamped: false }
  return { from: hireDate > to ? to : hireDate, to, clamped: true }
}
